import { useCallback, useEffect, useMemo, useState } from "react";
import { Editeur } from "./Editeur";
import { PanneauControles, useCorpus } from "./PanneauControles";
import { aJuger, controler } from "./controles";
import { oublierCorpus } from "./corpus";
import { ACTIONS, SIGNALEMENTS, STATUTS, date, etoiles, messageErreur, suitePourLesJoueurs } from "./libelles";
import { ErreurBase, appeler } from "./supabase";
import type { Fiche, Statut } from "./types";

type Mode = "lecture" | "correction" | "publication" | "retrait";

/**
 * La fiche d'un mot : sa définition, ce qu'en disent les joueurs, son
 * historique, et ce qu'on peut en faire — corriger, publier, retirer.
 */
export function FicheEcran(props: { readonly id: string; readonly onRetour: () => void }) {
  const [fiche, setFiche] = useState<Fiche | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>("lecture");
  const [annonce, setAnnonce] = useState<string | null>(null);

  const charger = useCallback(() => {
    setErreur(null);
    appeler<Fiche>("admin_fiche", { p_id: props.id })
      .then(setFiche)
      .catch((e: unknown) => setErreur(messageErreur(e instanceof ErreurBase ? e.code : "erreur_inconnue")));
  }, [props.id]);

  useEffect(charger, [charger]);

  const apres = (message: string) => {
    oublierCorpus();
    setMode("lecture");
    setAnnonce(message);
    charger();
  };

  if (fiche === null) {
    return (
      <section>
        <button type="button" className="lien" onClick={props.onRetour}>
          ← Retour à la recherche
        </button>
        {erreur === null ? <p role="status">Chargement…</p> : <p role="alert" className="erreur">{erreur}</p>}
      </section>
    );
  }

  return (
    <article className="fiche">
      <button type="button" className="lien" onClick={props.onRetour}>
        ← Retour à la recherche
      </button>
      <header className="fiche-tete">
        <h1>{fiche.mot}</h1>
        <span className={`pastille ${fiche.statut}`}>{STATUTS[fiche.statut]}</span>
        {fiche.reservee && <span className="pastille reservee">Réservé aux parties classées</span>}
        <span className="discret">{fiche.id}</span>
      </header>
      {annonce !== null && (
        <p role="status" className="succes">
          {annonce}
        </p>
      )}

      {mode === "correction" ? (
        <section>
          <h2>Correction</h2>
          <Editeur entree={fiche} onEnregistre={() => apres(suitePourLesJoueurs(fiche, "correction"))} onAnnuler={() => setMode("lecture")} />
        </section>
      ) : (
        <>
          <blockquote className="definition">{fiche.definition}</blockquote>
          <dl className="details">
            <dt>Variantes acceptées</dt>
            <dd>{fiche.variantes.length === 0 ? "aucune" : fiche.variantes.join(", ")}</dd>
            <dt>Difficulté</dt>
            <dd>{fiche.difficulte} / 5</dd>
            <dt>Relecteur</dt>
            <dd>{fiche.relecteur ?? "—"}</dd>
            <dt>Rédaction</dt>
            <dd>{fiche.origine === "assistee_ia" ? "assistée par IA, relue par un humain" : "humaine"}</dd>
            <dt>Publiée le</dt>
            <dd>{date(fiche.approuvee_le)}</dd>
            <dt>Modifiée le</dt>
            <dd>{date(fiche.modifie_le)}</dd>
            {fiche.note_culturelle !== null && (
              <>
                <dt>Note culturelle</dt>
                <dd>{fiche.note_culturelle}</dd>
              </>
            )}
          </dl>
          {mode === "lecture" && (
            <div className="actions">
              <button type="button" className="principal" onClick={() => setMode("correction")}>
                Corriger
              </button>
              {fiche.statut !== "approuvee" && (
                <button type="button" onClick={() => setMode("publication")}>
                  Publier
                </button>
              )}
              {fiche.statut === "brouillon" && (
                <ChangerStatut fiche={fiche} statut="en_revue" libelle="Mettre en revue" onFait={apres} />
              )}
              {fiche.statut !== "retiree" && (
                <button type="button" className="danger" onClick={() => setMode("retrait")}>
                  Retirer
                </button>
              )}
            </div>
          )}
          {mode === "publication" && <Publication fiche={fiche} onFait={apres} onAnnuler={() => setMode("lecture")} />}
          {mode === "retrait" && <Retrait fiche={fiche} onFait={apres} onAnnuler={() => setMode("lecture")} />}
        </>
      )}

      <AvisJoueurs fiche={fiche} />
      <Historique fiche={fiche} />
    </article>
  );
}

function ChangerStatut(props: {
  readonly fiche: Fiche;
  readonly statut: Statut;
  readonly libelle: string;
  readonly onFait: (message: string) => void;
}) {
  const [erreur, setErreur] = useState<string | null>(null);
  return (
    <>
      <button
        type="button"
        onClick={() =>
          void appeler("admin_changer_statut", { p_id: props.fiche.id, p_statut: props.statut })
            .then(() => props.onFait(`Statut : ${STATUTS[props.statut]}.`))
            .catch((e: unknown) => setErreur(messageErreur(e instanceof ErreurBase ? e.code : "erreur_inconnue")))
        }
      >
        {props.libelle}
      </button>
      {erreur !== null && (
        <p role="alert" className="erreur">
          {erreur}
        </p>
      )}
    </>
  );
}

/**
 * Publier : les contrôles sont rejoués sur la définition telle qu'elle est en
 * base, l'originalité est confirmée par l'administrateur (ADR-0002), et les
 * signaux de facilité doivent avoir été vus.
 */
function Publication(props: { readonly fiche: Fiche; readonly onFait: (message: string) => void; readonly onAnnuler: () => void }) {
  const { fiche } = props;
  const corpus = useCorpus();
  const [confirme, setConfirme] = useState(false);
  const [original, setOriginal] = useState(fiche.originalite_confirmee);
  const [motif, setMotif] = useState("");
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const controles = useMemo(
    () => controler({ id: fiche.id, mot: fiche.mot, definition: fiche.definition, variantes: fiche.variantes }, corpus),
    [fiche, corpus],
  );
  const bloque = envoi || controles.echecs.length > 0 || !original || (aJuger(controles) && !confirme);

  const publier = async () => {
    setEnvoi(true);
    setErreur(null);
    try {
      await appeler("admin_changer_statut", {
        p_id: fiche.id,
        p_statut: "approuvee",
        p_motif: motif.trim() === "" ? null : motif,
        p_facilite_vue: confirme,
        p_originalite_confirmee: original,
      });
      props.onFait(suitePourLesJoueurs(fiche, "publication"));
    } catch (e) {
      setErreur(messageErreur(e instanceof ErreurBase ? e.code : "erreur_inconnue"));
      setEnvoi(false);
    }
  };

  return (
    <section className="panneau" aria-label="Publication">
      <h2>Publier « {fiche.mot} »</h2>
      <PanneauControles controles={controles} corpusCharge={corpus !== null} confirme={confirme} onConfirme={setConfirme} />
      {controles.echecs.length > 0 && <p className="erreur">Corrige d'abord la définition : elle casse une règle du dépôt.</p>}
      <label className="case">
        <input type="checkbox" checked={original} onChange={(e) => setOriginal(e.target.checked)} />
        Définition originale, écrite pour LexiReveal et non reprise d'un dictionnaire
      </label>
      <label>
        Motif (facultatif)
        <input value={motif} onChange={(e) => setMotif(e.target.value)} />
      </label>
      {erreur !== null && (
        <p role="alert" className="erreur">
          {erreur}
        </p>
      )}
      <div className="actions">
        <button type="button" className="principal" disabled={bloque} onClick={() => void publier()}>
          Confirmer la publication
        </button>
        <button type="button" onClick={props.onAnnuler}>
          Annuler
        </button>
      </div>
    </section>
  );
}

function Retrait(props: { readonly fiche: Fiche; readonly onFait: (message: string) => void; readonly onAnnuler: () => void }) {
  const [motif, setMotif] = useState("");
  const [erreur, setErreur] = useState<string | null>(null);

  const retirer = async () => {
    setErreur(null);
    try {
      await appeler("admin_changer_statut", { p_id: props.fiche.id, p_statut: "retiree", p_motif: motif });
      props.onFait(suitePourLesJoueurs(props.fiche, "retrait"));
    } catch (e) {
      setErreur(messageErreur(e instanceof ErreurBase ? e.code : "erreur_inconnue"));
    }
  };

  return (
    <section className="panneau" aria-label="Retrait">
      <h2>Retirer « {props.fiche.mot} »</h2>
      <p className="discret">Le mot quitte le corpus publié ; il reste en base, avec son historique, et peut être republié.</p>
      <label>
        Motif du retrait
        <input value={motif} onChange={(e) => setMotif(e.target.value)} required />
      </label>
      {erreur !== null && (
        <p role="alert" className="erreur">
          {erreur}
        </p>
      )}
      <div className="actions">
        <button type="button" className="danger" disabled={motif.trim() === ""} onClick={() => void retirer()}>
          Confirmer le retrait
        </button>
        <button type="button" onClick={props.onAnnuler}>
          Annuler
        </button>
      </div>
    </section>
  );
}

function AvisJoueurs({ fiche }: { readonly fiche: Fiche }) {
  const { notes, signalements } = fiche.avis;
  const repartition = Object.entries(notes.repartition).sort(([a], [b]) => Number(b) - Number(a));
  return (
    <section aria-labelledby="titre-avis">
      <h2 id="titre-avis">Ce qu'en disent les joueurs</h2>
      {notes.nombre === 0 && signalements.length === 0 && <p className="discret">Ni note ni signalement pour l'instant.</p>}
      {notes.nombre > 0 && (
        <p>
          Note moyenne <strong>{etoiles(notes.moyenne)}</strong> sur {notes.nombre} note{notes.nombre > 1 ? "s" : ""}
          {" — "}
          {repartition.map(([valeur, nombre]) => `${Number(valeur).toLocaleString("fr-FR")} ★ × ${nombre}`).join(", ")}
        </p>
      )}
      {signalements.length > 0 && (
        <ul aria-label="Signalements">
          {signalements.map((s) => (
            <li key={s.motif}>
              {SIGNALEMENTS[s.motif] ?? s.motif} : <strong>{s.nombre}</strong> (dernier le {date(s.dernier)})
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Historique({ fiche }: { readonly fiche: Fiche }) {
  return (
    <section aria-labelledby="titre-historique">
      <h2 id="titre-historique">Historique</h2>
      <ol className="historique" aria-label="Versions">
        {fiche.historique.map((v) => (
          <li key={v.version}>
            <p>
              <strong>Version {v.version}</strong> · {ACTIONS[v.action]} · {STATUTS[v.statut]} · {date(v.le)} · par {v.par}
            </p>
            <p className="definition-ancienne">{v.definition}</p>
            {v.motif !== null && <p className="discret">Motif : {v.motif}</p>}
          </li>
        ))}
      </ol>
    </section>
  );
}
